// frontend/src/api/researchService.ts
import apiClient from './index';
import type { 
    Research, 
    ResearchListed, 
    ResearchUpdateJsonPayload,
    CountResponse
} from '@/types';
import type { AxiosError } from 'axios';
import axios from 'axios';

interface FetchResearchParams {
  skip?: number;
  limit?: number;
  status?: 'draft' | 'published' | 'archived' | 'all';
  tags?: string[];
  search?: string;
}

export const getResearchItems = async (params?: FetchResearchParams): Promise<ResearchListed[]> => {
    try {
        const response = await apiClient.get<ResearchListed[]>('/research/', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching research items:', error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error("An unknown error occurred while fetching research items.");
    }
};

export const getResearchItemsCount = async (): Promise<CountResponse> => {
  try {
    const response = await apiClient.get<CountResponse>('/research/count');
    return response.data;
  } catch (error) {
    console.error("Error fetching research items count:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching research count.");
  }
};

export const getResearchItemById = async (researchId: number): Promise<Research> => {
    try {
        const response = await apiClient.get<Research>(`/research/${researchId}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching research item ${researchId}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error(`An unknown error occurred while fetching research item ${researchId}.`);
    }
};

export const createResearchItem = async (formData: FormData): Promise<Research> => {
    try {
        const response = await apiClient.post<Research>('/research/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating research item:', error);
         if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw new Error("An unknown error occurred while creating research item.");
    }
};

export const updateResearchItem = async (  researchId: number,
  jsonData: ResearchUpdateJsonPayload,
  pdfFile?: File | null
): Promise<Research> => {
  const formData = new FormData();

  if (jsonData.title !== undefined) formData.append('title', jsonData.title);
  if (jsonData.description !== undefined) formData.append('description', jsonData.description || ''); // Send empty string if description is cleared
  if (jsonData.status !== undefined) formData.append('status', jsonData.status);
  
  if (jsonData.tag_ids !== undefined) {
    formData.append('tag_ids_json', JSON.stringify(jsonData.tag_ids || [])); // Send empty array if tags are cleared
  }

  if (pdfFile) {
    formData.append('pdf_file', pdfFile, pdfFile.name);
  }
  
  const response = await apiClient.put<Research>(`/research/${researchId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteResearchItem = async (id: number): Promise<void> => {
    try {
        await apiClient.delete(`/research/${id}`);
    } catch (error) {
        console.error(`Error deleting research item ${id}:`, error);
        if (axios.isAxiosError(error)) {
            const apiError = error as AxiosError<{ detail?: string }>;
            if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
        }
        throw error;
    }
};
